import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
  consent: false,
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm consent to proceed.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your message has reached us.", {
        description: "We'll get back to you on the email you shared.",
      });
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="ct-container scroll-mt-28 py-24 md:py-32"
    >
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="ct-eyebrow">Contact</span>
          <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight text-[#0F172A] sm:text-5xl">
            Let's build
            <br />
            <span className="text-neutral-700">something useful.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600">
            For partnerships, education collaborations, or general queries — reach out.
            We read every message.
          </p>

          <ul className="mt-10 space-y-3">
            <li>
              <a
                href="mailto:info@contentiumtech.com"
                data-testid="contact-email-link"
                className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-neutral-700/60 hover:bg-neutral-50/40"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-neutral-50 text-neutral-700">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Email</p>
                  <p className="font-display text-base font-semibold text-[#0F172A]">
                    info@contentiumtech.com
                  </p>
                </div>
              </a>
            </li>
            <li>
              <a
                href="tel:+919654006470"
                data-testid="contact-phone-link"
                className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-neutral-700/60 hover:bg-neutral-50/40"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-neutral-50 text-neutral-700">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Phone</p>
                  <p className="font-display text-base font-semibold text-[#0F172A]">
                    +91 96540 06470
                  </p>
                </div>
              </a>
            </li>
            <li>
              <a
                href="https://wa.me/919654006470"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-neutral-700/60 hover:bg-neutral-50/40"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-neutral-50 text-neutral-700">
                  <MessageCircle className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">WhatsApp</p>
                  <p className="font-display text-base font-semibold text-[#0F172A]">
                    Chat with us
                  </p>
                </div>
              </a>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="rounded-3xl border border-slate-200 bg-white p-7 md:p-10"
          >
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-[#0F172A]">
                  Name <span className="text-neutral-700">*</span>
                </Label>
                <Input
                  id="name"
                  data-testid="contact-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  className="mt-2 h-11 border-slate-300 bg-white text-[#0F172A] focus-visible:ring-neutral-600"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-[#0F172A]">
                  Email <span className="text-neutral-700">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className="mt-2 h-11 border-slate-300 bg-white text-[#0F172A] focus-visible:ring-neutral-600"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-[#0F172A]">
                  Phone (optional)
                </Label>
                <Input
                  id="phone"
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 ..."
                  className="mt-2 h-11 border-slate-300 bg-white text-[#0F172A] focus-visible:ring-neutral-600"
                />
              </div>
              <div>
                <Label htmlFor="subject" className="text-sm font-medium text-[#0F172A]">
                  Subject
                </Label>
                <Input
                  id="subject"
                  data-testid="contact-subject"
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="What is this about?"
                  className="mt-2 h-11 border-slate-300 bg-white text-[#0F172A] focus-visible:ring-neutral-600"
                />
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="message" className="text-sm font-medium text-[#0F172A]">
                Message <span className="text-neutral-700">*</span>
              </Label>
              <Textarea
                id="message"
                data-testid="contact-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us a bit about your enquiry..."
                rows={5}
                className="mt-2 border-slate-300 bg-white text-[#0F172A] focus-visible:ring-neutral-600"
                required
              />
            </div>

            <div className="mt-6 flex items-start gap-3">
              <Checkbox
                id="consent"
                data-testid="contact-consent"
                checked={form.consent}
                onCheckedChange={(v) => setForm((f) => ({ ...f, consent: Boolean(v) }))}
                className="mt-0.5 border-slate-400 data-[state=checked]:bg-neutral-700 data-[state=checked]:border-neutral-700"
              />
              <Label
                htmlFor="consent"
                className="text-sm leading-relaxed text-slate-600"
              >
                I consent to Contentium Tech storing my submitted information to
                respond to my enquiry, in accordance with the Privacy Policy and the
                Digital Personal Data Protection Act, 2023.
              </Label>
            </div>

            <Button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="mt-8 h-12 w-full rounded-full bg-[#0A0A0A] text-base font-semibold text-white hover:bg-[#262626] md:w-auto md:px-10"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  Send message <Send className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
